/**
 * Panel „Role i uprawnienia" [D11] — nadawanie ról użytkownikom obszaru
 * i rozpatrywanie próśb o dostęp (spec 05 przepływ „join").
 *
 * Widok jest w całości oparty na realnych danych tenanta: brak próśb pokazuje
 * uczciwy stan pusty, nie listę przykładową [D19].
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Badge, Button, Card, EmptyState, Inbox, Select, Spinner, StatusPill, cx } from '@k1p4/ui'
import { ROLES } from '@k1p4/shared'
import { ApiError, api } from '../../api/client'
import styles from './RolesPanel.module.css'

const USERS_KEY = ['tenant', 'users'] as const
const JOIN_KEY = ['tenant', 'join-requests'] as const

export function RolesPanel() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [draftRoles, setDraftRoles] = useState<Record<string, string[]>>({})
  const [joinRole, setJoinRole] = useState<Record<string, string>>({})

  const users = useQuery({ queryKey: USERS_KEY, queryFn: () => api.users() })
  const joinRequests = useQuery({ queryKey: JOIN_KEY, queryFn: () => api.joinRequests() })

  const saveRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => api.setUserRoles(id, roles),
    onSuccess: async (_result, variables) => {
      setError(null)
      setDraftRoles((current) => {
        const next = { ...current }
        delete next[variables.id]
        return next
      })
      await queryClient.invalidateQueries({ queryKey: USERS_KEY })
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiError && caught.code === 'LAST_ADMIN'
          ? 'roles.lastAdmin'
          : 'auth.errorGeneric',
      )
    },
  })

  const decide = useMutation({
    mutationFn: ({ id, approve, role }: { id: string; approve: boolean; role: string }) =>
      approve ? api.approveJoinRequest(id, role) : api.rejectJoinRequest(id),
    onSuccess: async () => {
      setError(null)
      await queryClient.invalidateQueries({ queryKey: JOIN_KEY })
      await queryClient.invalidateQueries({ queryKey: USERS_KEY })
    },
    onError: () => setError('auth.errorGeneric'),
  })

  if (users.isPending || joinRequests.isPending) {
    return <Spinner label={t('common.loading')} />
  }

  const pending = (joinRequests.data?.items ?? []).filter((item) => item.status === 'pending')

  return (
    <>
      {error !== null ? (
        <p className={styles.error} role="alert">
          {t(error)}
        </p>
      ) : null}

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>{t('roles.joinHeading')}</h2>
        <Card pad="lg">
          {pending.length === 0 ? (
            <EmptyState
              icon={<Inbox size={26} />}
              title={t('roles.noJoinRequests')}
              description={t('empty.generic')}
            />
          ) : (
            pending.map((request) => {
              const selected = joinRole[request.id] ?? 'employee'
              return (
                <div key={request.id} className={styles.row}>
                  <div className={styles.identity}>
                    <p className={styles.email}>{request.email}</p>
                    <p className={styles.meta}>
                      <span>{request.codeOrDomain}</span>
                      <StatusPill tone="pink" dot>
                        {t('roles.statusPending')}
                      </StatusPill>
                    </p>
                  </div>
                  <Select
                    className={styles.roleSelect}
                    aria-label={t('roles.roleLabel')}
                    value={selected}
                    onChange={(event) =>
                      setJoinRole((current) => ({ ...current, [request.id]: event.target.value }))
                    }
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {t(`roleNames.${role}`)}
                      </option>
                    ))}
                  </Select>
                  <div className={styles.actions}>
                    <Button
                      size="sm"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ id: request.id, approve: true, role: selected })
                      }
                    >
                      {t('roles.approve')}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ id: request.id, approve: false, role: selected })
                      }
                    >
                      {t('roles.reject')}
                    </Button>
                  </div>
                </div>
              )
            })
          )}
        </Card>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionHeading}>{t('roles.usersHeading')}</h2>
        <Card pad="lg">
          {(users.data?.items ?? []).map((user) => {
            const current = draftRoles[user.id] ?? user.roles
            const dirty = draftRoles[user.id] !== undefined
            return (
              <div key={user.id} className={styles.row}>
                <div className={styles.identity}>
                  <p className={styles.email}>{user.email}</p>
                  <p className={styles.meta}>
                    {user.emailVerified ? null : (
                      <Badge tone="soft">{t('roles.emailUnverified')}</Badge>
                    )}
                  </p>
                </div>
                <div className={styles.roles}>
                  {ROLES.map((role) => {
                    const active = current.includes(role)
                    return (
                      <button
                        key={role}
                        type="button"
                        className={cx(styles.roleToggle, active && styles.roleToggleActive)}
                        aria-pressed={active}
                        onClick={() =>
                          setDraftRoles((state) => ({
                            ...state,
                            [user.id]: active
                              ? current.filter((entry) => entry !== role)
                              : [...current, role],
                          }))
                        }
                      >
                        {t(`roleNames.${role}`)}
                      </button>
                    )
                  })}
                </div>
                <div className={styles.actions}>
                  <Button
                    size="sm"
                    disabled={!dirty || current.length === 0 || saveRoles.isPending}
                    onClick={() => saveRoles.mutate({ id: user.id, roles: current })}
                  >
                    {t('roles.save')}
                  </Button>
                </div>
              </div>
            )
          })}
        </Card>
      </section>
    </>
  )
}
